import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { GasProduct, GasPickupPoint } from '@tucupet/shared';
import { PageHeader, Table, Btn, Modal, Field, Select } from '../components/ui';
import { api, ApiError } from '../lib/api';

const ACTIVE_OPTS = [
  { value: 'true', label: 'Activo' },
  { value: 'false', label: 'Inactivo' },
];

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="mono" style={{ fontSize: 10, padding: '2px 7px', borderRadius: 999, background: active ? 'color-mix(in oklab, var(--accent) 14%, transparent)' : 'var(--surface-2)', color: active ? 'var(--accent)' : 'var(--text-3)' }}>
      {active ? 'ACTIVO' : 'INACTIVO'}
    </span>
  );
}

export default function GasCatalogPage() {
  return (
    <div>
      <Link to="/gas" className="mono" style={{ color: 'var(--text-3)', fontSize: 12, textDecoration: 'none' }}>← Pedidos de gas</Link>
      <div style={{ height: 12 }} />
      <PageHeader kicker="GAS / BALITAS" title="Catálogo" sub="Productos (balitas y accesorios) y puntos de recogida." />
      <ProductsSection />
      <div style={{ height: 28 }} />
      <PickupsSection />
    </div>
  );
}

// ── Products ──────────────────────────────────────────────────────────────────

function ProductsSection() {
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<Partial<GasProduct> | null>(null);
  const { data } = useQuery<{ items: GasProduct[] }>({ queryKey: ['admin', 'gas', 'products'], queryFn: () => api('/api/admin/gas/products') });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'gas', 'products'] });

  const save = useMutation({
    mutationFn: (p: Partial<GasProduct>) => {
      const payload = { name: p.name, description: p.description ?? '', kind: p.kind, price: Number(p.price), order: Number(p.order ?? 0), active: p.active ?? true };
      return p.id ? api(`/api/admin/gas/products/${p.id}`, { method: 'PATCH', body: payload }) : api('/api/admin/gas/products', { method: 'POST', body: payload });
    },
    onSuccess: () => { setEditing(null); invalidate(); },
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/api/admin/gas/products/${id}`, { method: 'DELETE' }), onSuccess: invalidate });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 className="display" style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Productos · {data?.items.length ?? 0}</h2>
        <Btn size="sm" onClick={() => setEditing({ name: '', kind: 'cylinder', price: 0, description: '', order: 0, active: true })}>+ Producto</Btn>
      </div>
      <Table<GasProduct>
        rows={data?.items ?? []}
        empty="Sin productos"
        columns={[
          { key: 'name', label: 'Nombre', render: (r) => <span>{r.name}</span> },
          { key: 'kind', label: 'Tipo', render: (r) => <span className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>{r.kind === 'cylinder' ? 'BALITA' : 'ACCESORIO'}</span> },
          { key: 'price', label: 'Precio', render: (r) => <span className="mono" style={{ fontWeight: 600 }}>${r.price.toFixed(2)}</span> },
          { key: 'order', label: 'Orden', render: (r) => <span className="mono" style={{ fontSize: 11 }}>{r.order}</span> },
          { key: 'active', label: 'Estado', render: (r) => <ActiveBadge active={r.active} /> },
          {
            key: 'actions',
            label: '',
            render: (r) => (
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <Btn size="sm" variant="ghost" onClick={() => setEditing(r)}>Editar</Btn>
                <Btn size="sm" variant="danger" onClick={() => confirm(`¿Eliminar ${r.name}?`) && remove.mutate(r.id)}>Borrar</Btn>
              </div>
            ),
          },
        ]}
      />
      {editing && (
        <ProductModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(p) => save.mutate(p)}
          pending={save.isPending}
          error={save.isError ? (save.error as ApiError)?.message : null}
        />
      )}
    </div>
  );
}

function ProductModal({ initial, onClose, onSubmit, pending, error }: { initial: Partial<GasProduct>; onClose: () => void; onSubmit: (p: Partial<GasProduct>) => void; pending: boolean; error: string | null }) {
  const [f, setF] = React.useState<Partial<GasProduct>>(initial);
  const set = (k: keyof GasProduct) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const valid = (f.name ?? '').trim() && Number(f.price) > 0;
  return (
    <Modal open onClose={onClose} title={initial.id ? 'Editar producto' : 'Nuevo producto'} footer={<><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn onClick={() => onSubmit(f)} disabled={!valid || pending}>{pending ? 'Guardando…' : 'Guardar'}</Btn></>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="Nombre" value={f.name ?? ''} onChange={set('name')} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Select label="Tipo" value={(f.kind as string) ?? 'cylinder'} onChange={(v) => setF((s) => ({ ...s, kind: v as GasProduct['kind'] }))} options={[{ value: 'cylinder', label: 'Balita' }, { value: 'accessory', label: 'Accesorio' }]} />
          <Field label="Precio" type="number" step="0.01" value={String(f.price ?? '')} onChange={set('price')} />
          <Field label="Orden" type="number" value={String(f.order ?? 0)} onChange={set('order')} />
        </div>
        <Field label="Descripción" value={f.description ?? ''} onChange={set('description')} />
        <Select label="Estado" value={String(f.active ?? true)} onChange={(v) => setF((s) => ({ ...s, active: v === 'true' }))} options={ACTIVE_OPTS} />
        {error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
      </div>
    </Modal>
  );
}

// ── Pickup points ─────────────────────────────────────────────────────────────

function PickupsSection() {
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<Partial<GasPickupPoint> | null>(null);
  const { data } = useQuery<{ items: GasPickupPoint[] }>({ queryKey: ['admin', 'gas', 'pickups'], queryFn: () => api('/api/admin/gas/pickup-points') });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'gas', 'pickups'] });

  const save = useMutation({
    mutationFn: (p: Partial<GasPickupPoint>) => {
      const payload = { name: p.name, addr: p.addr, provincia: p.provincia, municipio: p.municipio, lat: Number(p.lat), lng: Number(p.lng), hours: p.hours || '08–17', phone: p.phone || null, active: p.active ?? true };
      return p.id ? api(`/api/admin/gas/pickup-points/${p.id}`, { method: 'PATCH', body: payload }) : api('/api/admin/gas/pickup-points', { method: 'POST', body: payload });
    },
    onSuccess: () => { setEditing(null); invalidate(); },
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/api/admin/gas/pickup-points/${id}`, { method: 'DELETE' }), onSuccess: invalidate });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 className="display" style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Puntos de recogida · {data?.items.length ?? 0}</h2>
        <Btn size="sm" onClick={() => setEditing({ name: '', addr: '', provincia: '', municipio: '', lat: 0, lng: 0, hours: '08–17', phone: '', active: true })}>+ Punto</Btn>
      </div>
      <Table<GasPickupPoint>
        rows={data?.items ?? []}
        empty="Sin puntos de recogida"
        columns={[
          { key: 'name', label: 'Nombre', render: (r) => <span>{r.name}</span> },
          { key: 'loc', label: 'Ubicación', render: (r) => <span style={{ fontSize: 12 }}>{[r.municipio, r.provincia].filter(Boolean).join(', ')}</span> },
          { key: 'addr', label: 'Dirección', render: (r) => <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{r.addr}</span> },
          { key: 'hours', label: 'Horario', render: (r) => <span className="mono" style={{ fontSize: 11 }}>{r.hours}</span> },
          { key: 'active', label: 'Estado', render: (r) => <ActiveBadge active={r.active} /> },
          {
            key: 'actions',
            label: '',
            render: (r) => (
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <Btn size="sm" variant="ghost" onClick={() => setEditing(r)}>Editar</Btn>
                <Btn size="sm" variant="danger" onClick={() => confirm(`¿Eliminar ${r.name}?`) && remove.mutate(r.id)}>Borrar</Btn>
              </div>
            ),
          },
        ]}
      />
      {editing && (
        <PickupModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(p) => save.mutate(p)}
          pending={save.isPending}
          error={save.isError ? (save.error as ApiError)?.message : null}
        />
      )}
    </div>
  );
}

function PickupModal({ initial, onClose, onSubmit, pending, error }: { initial: Partial<GasPickupPoint>; onClose: () => void; onSubmit: (p: Partial<GasPickupPoint>) => void; pending: boolean; error: string | null }) {
  const [f, setF] = React.useState<Partial<GasPickupPoint>>(initial);
  const set = (k: keyof GasPickupPoint) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const valid = (f.name ?? '').trim() && (f.addr ?? '').trim() && (f.provincia ?? '').trim() && (f.municipio ?? '').trim();
  return (
    <Modal open onClose={onClose} title={initial.id ? 'Editar punto' : 'Nuevo punto de recogida'} footer={<><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn onClick={() => onSubmit(f)} disabled={!valid || pending}>{pending ? 'Guardando…' : 'Guardar'}</Btn></>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="Nombre" value={f.name ?? ''} onChange={set('name')} />
        <Field label="Dirección" value={f.addr ?? ''} onChange={set('addr')} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Provincia" value={f.provincia ?? ''} onChange={set('provincia')} />
          <Field label="Municipio" value={f.municipio ?? ''} onChange={set('municipio')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Latitud" type="number" step="0.0001" value={String(f.lat ?? '')} onChange={set('lat')} />
          <Field label="Longitud" type="number" step="0.0001" value={String(f.lng ?? '')} onChange={set('lng')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Horario" value={f.hours ?? ''} onChange={set('hours')} placeholder="08–17" />
          <Field label="Teléfono" value={f.phone ?? ''} onChange={set('phone')} />
        </div>
        <Select label="Estado" value={String(f.active ?? true)} onChange={(v) => setF((s) => ({ ...s, active: v === 'true' }))} options={ACTIVE_OPTS} />
        {error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
      </div>
    </Modal>
  );
}
